import React from 'react';
import { Delete as DeleteIcon } from '@mui/icons-material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import WorkIcon from '@mui/icons-material/Work';
import EditNoteIcon from '@mui/icons-material/EditNote';
import CloseIcon from '@mui/icons-material/Close';
import dayjs from 'dayjs';
import { fmtNum, calcOtForDay } from '../utils';

const fmt = (n, d = 2) => fmtNum(n, d);

// เตือนถ้าอัตราค่าแรง/วันที่กรอกต่างจากค่าปกติของพนักงานคนนั้นเกิน 5 เท่า (กันพิมพ์เลขผิด/เกินขอบเขต)
const DR_SUSPICIOUS_MULTIPLIER = 5;

const RATE_OPTIONS = [
  { value: 1, label: '×1.0 — วันทำงานปกติ' },
  { value: 1.5, label: '×1.5 — อัตราพิเศษ' },
  { value: 2, label: '×2.0 — วันหยุด' },
  { value: 3, label: '×3.0 — วันหยุดนักขัตฤกษ์' },
];

const OV = { position: 'fixed', inset: 0, zIndex: 1300, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center' };
const MO = { background: '#f8fafc', borderRadius: 8, maxWidth: 860, width: 'calc(100% - 32px)', maxHeight: '92vh', overflowY: 'auto', display: 'flex', flexDirection: 'column' };
const IS = { width: '100%', boxSizing: 'border-box', padding: '6px 10px', borderRadius: 4, border: '1px solid #d1d5db', fontSize: '0.875rem', outline: 'none', background: '#fff' };
const BP = { padding: '6px 14px', borderRadius: 20, border: 'none', backgroundColor: '#2563eb', color: '#fff', cursor: 'pointer', fontSize: '0.8125rem', fontWeight: 600 };
const BC = { padding: '5px 14px', borderRadius: 20, border: '1px solid #d1d5db', backgroundColor: '#fff', color: '#374151', cursor: 'pointer', fontSize: '0.8125rem' };
const BO = { padding: '5px 14px', borderRadius: 20, border: '1px solid #fca5a5', backgroundColor: '#fff', color: '#d32f2f', cursor: 'pointer', fontSize: '0.8125rem' };
const BW = { padding: '5px 14px', borderRadius: 20, border: '1px solid #fcd34d', backgroundColor: '#fff', color: '#b45309', cursor: 'pointer', fontSize: '0.8125rem' };
const BG = { padding: '5px 14px', borderRadius: 20, border: '1px solid #86efac', backgroundColor: '#fff', color: '#15803d', cursor: 'pointer', fontSize: '0.8125rem' };

// noMargin: ใช้ตอน FRow อยู่ในแถวที่ alignItems:'end' คู่กับปุ่มบันทึก — marginBottom เดิม
// จะไปดันความสูงของ track ทำให้ปุ่มเลื่อนต่ำกว่าขอบล่างของ input จริง
const FRow = ({ label, children, noMargin }) => (
  <div style={{ marginBottom: noMargin ? 0 : 10 }}>
    <label style={{ display: 'block', fontSize: '0.72rem', color: '#666', marginBottom: 3, fontWeight: 500 }}>{label}</label>
    {children}
  </div>
);

const SecLabel = ({ txt, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
    <div style={{ width: 8, height: 8, borderRadius: '50%', background: color, flexShrink: 0 }} />
    <span style={{ fontSize: '0.62rem', fontWeight: 800, color, textTransform: 'uppercase', letterSpacing: '0.08em' }}>{txt}</span>
    <div style={{ flex: 1, height: 1, background: color, opacity: 0.15 }} />
  </div>
);

const StatCard = ({ label, amount, formula, extra, color, strikeAmt }) => (
  <div style={{
    borderRadius: 6, padding: '10px 12px', border: `1.5px solid ${color}33`,
    background: `linear-gradient(135deg, ${color}14 0%, #fff 100%)`,
    borderLeft: `4px solid ${color}`, minHeight: 88,
    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
  }}>
    <div style={{ fontSize: '0.58rem', fontWeight: 800, color: `${color}cc`, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 4 }}>{label}</div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexWrap: 'wrap' }}>
      {strikeAmt != null && <span style={{ fontSize: '0.7rem', color: '#9ca3af', textDecoration: 'line-through' }}>฿{fmt(strikeAmt)}</span>}
      <span style={{ fontSize: '1rem', fontWeight: 800, color }}>{fmt(amount)}</span>
    </div>
    {formula && <div style={{ fontSize: '0.58rem', color: `${color}99`, marginTop: 2, fontFamily: 'monospace', lineHeight: 1.35 }}>{formula}</div>}
    {extra && <div style={{ fontSize: '0.58rem', color: '#64748b', marginTop: 2, lineHeight: 1.35 }}>{extra}</div>}
  </div>
);

const InfoPanel = ({ children, border, bg }) => (
  <div style={{ border: `1px solid ${border}`, borderRadius: 6, padding: '10px 14px', background: bg, marginBottom: 0 }}>{children}</div>
);

const SalaryOverrideDialog = React.memo(function SalaryOverrideDialog({
  open, onClose, detailData,
  editAmtManual, setEditAmtManual, detailRate, setDetailRate,
  detailHours, forceFullHours, setForceFullHours,
  saving, skipLateSaving, checkTimeSaving,
  editCheckIn, setEditCheckIn, editCheckOut, setEditCheckOut,
  editProjectId, setEditProjectId,
  dlgFieldAllow, attendanceRules, salaryMode, hrMgmt, projectList,
  getEntry, onSalaryOverride, onSkipLateDeduct, onCheckInTimeClear, onProjectClear,
  onHourOverride, onOtOverride, onManualDelete,
  saveOverride, clearOverride, saveSkipLate, clearTimeOverride, clearProjectOverride,
  deleteWorkHours, restoreWorkHours, clearForceFullHours, deleteOtHours, restoreOtHours,
  deleteManualFromDialog, saveCheckInTime, saveProject, saveFieldAllowDay,
}) {
  const dlgC = detailData?.checkin;
  const dlgDr = detailData?.spd || dlgC?.salary_per_day || 0;
  const dlgAuto = dlgC?.salary_override?.amount != null
    ? dlgC.salary_override.amount
    : dlgC?.totalHour != null
    ? Number(((dlgC.totalHour / 8) * (dlgC?.effectiveRate || 1) * dlgDr).toFixed(2))
    : 0;
  const dlgOvr = dlgC?.salary_override?.amount != null;
  const dlgTimeOvr = dlgC?.time_override != null;
  // project_override.original_project_id มี default:null ใน schema — เช็ค edited_by แทน (ไม่มี default)
  const dlgProjOvr = dlgC?.project_override?.edited_by != null;
  const dlgHoursDeleted = dlgC?.hour_override?.hours === 0;
  const dlgOtDisabled = dlgC?.ot_override?.disabled === true;
  const dlgHasOt = (dlgC?.otRequests?.total_hours || 0) > 0;
  const dlgIsSkipped = dlgC?.skip_late_deduct === true;

  // ── การ์ด "เพิ่มโดย" (ฟ้า) กับ "แก้ไขโดย" (เหลือง) เป็น 2 เหตุการณ์แยกกัน โชว์พร้อมกันได้ ──
  // checkin ที่ HR เพิ่มผ่านฟอร์ม "เพิ่มรายการลงเวลา" (AddTimestampTime.js) — note ขึ้นต้นด้วย
  // "Check in by ..." หรือ "Check in/out by ..." — ดึงชื่อคนเพิ่มจาก note
  const hrAddMatch = /^Check in(?:\/out)? by (.+)$/.exec(dlgC?.note || '');
  const dlgIsHrManualCheckin = !dlgC?.is_manual && !!hrAddMatch;
  const dlgShowAddedCard = !!dlgC?.is_manual || dlgIsHrManualCheckin;
  const dlgAddedByLabel = dlgC?.is_manual ? 'เพิ่มชั่วโมงการทำงาน' : 'เพิ่มรายการลงเวลาโดย HR';
  // is_manual: ใช้ added_by/added_at (แยกจาก salary_override — กันหายตอนแก้ไขค่าแรงซ้ำทีหลัง)
  // fallback ไป salary_override.edited_by/edited_at สำหรับ record เก่าที่สร้างก่อนมี field นี้
  const dlgAddedBy = dlgC?.is_manual
    ? dlgC?.added_by || dlgC?.salary_override?.edited_by || null
    : hrAddMatch?.[1]?.trim() || null;
  const dlgAddedAt = dlgC?.is_manual
    ? dlgC?.added_at || dlgC?.salary_override?.edited_at || null
    : dlgC?.createdAt || null;
  // "แก้ไขโดย" ครอบคลุมทั้ง 3 ทางที่แก้ไขได้: ค่าแรง (salary_override.edited_by — ไม่นับตอนสร้างจาก
  // AddHoursDialog ที่เก็บแยกไว้ที่ added_by/added_at แล้ว), เวลาเข้า-ออก (time_override), โปรเจค (project_override)
  // — แต่ละอย่างมีคนแก้/เวลาแก้เป็นของตัวเอง เลยต้องโชว์แยกเป็นรายการ ไม่ใช่การ์ดเดียวรวมกัน
  const dlgEditEntries = [
    dlgC?.salary_override?.edited_by && {
      label: 'ค่าแรง',
      by: dlgC.salary_override.edited_by,
      at: dlgC.salary_override.edited_at,
    },
    dlgTimeOvr && {
      label: 'เวลาเข้า-ออก',
      by: dlgC?.time_override?.edited_by,
      at: dlgC?.time_override?.edited_at,
    },
    dlgProjOvr && {
      label: 'โปรเจค',
      by: dlgC?.project_override?.edited_by,
      at: dlgC?.project_override?.edited_at,
    },
  ].filter(Boolean);
  const dlgHasEdit = dlgEditEntries.length > 0;

  const currentDr = parseFloat(editAmtManual ?? String(detailData?.spd || dlgDr || 0)) || 0;
  const calcAmt = detailRate * currentDr;
  const origDr = detailData?.spd || dlgDr || 0;
  const origAmt = dlgAuto;
  const drChanged = Math.abs(currentDr - origDr) > 0.01;
  // เตือนถ้าค่าแรง/วันที่กรอกต่างจากค่าปกติของพนักงานคนนี้เกิน DR_SUSPICIOUS_MULTIPLIER เท่า (กันพิมพ์ผิด)
  const isDrSuspicious =
    origDr > 0 &&
    currentDr > 0 &&
    (currentDr > origDr * DR_SUSPICIOUS_MULTIPLIER ||
      currentDr < origDr / DR_SUSPICIOUS_MULTIPLIER);
  const origRate = dlgC?.effectiveRate || dlgC?.rate || 1;
  const effChanged = detailRate !== origRate;
  const isModified = drChanged || effChanged;
  const hrRate = (currentDr * detailRate) / 8;
  const minRate = hrRate / 60;
  // ใช้ชม.เดียวกับ day cell: hour_override ถ้ามี (ลบ/บังคับชั่วโมง), ไม่งั้นใช้ totalHour
  const actualHr = dlgC?.hour_override?.hours ?? dlgC?.totalHour ?? null;
  // ชั่วโมงทำงานจริงดั้งเดิม (ไม่สนใจ hour_override) — ใช้แสดง "ทำงานจริง" เฉยๆ ไม่เอาไปคิดเงิน
  const rawActualHr = dlgC?.hour_override?.previous_hours ?? dlgC?.totalHour ?? null;
  const isHourForced = dlgC?.hour_override?.hours === 8;

  const dlgCit = dlgC?.checkInTime;
  const lateRuleD = (attendanceRules || []).find((r) => r.type === 'LATE_ARRIVAL' && r.enabled);
  let lateMin = 0, effMin = 0, lateAmt = 0;
  if (dlgCit && !dlgC?.is_manual) {
    const [ch, cm] = dlgCit.split(':').map(Number);
    if (ch < 12) {
      lateMin = Math.max(0, ch * 60 + cm - 8 * 60);
      if (lateMin > 0) {
        if (!lateRuleD) effMin = lateMin;
        else if (lateMin >= lateRuleD.threshold_minutes) effMin = Math.max(lateMin, lateRuleD.deduct_minutes || 0);
        if (effMin > 0 && !dlgIsSkipped) lateAmt = Number((effMin * minRate).toFixed(2));
      }
    }
  }
  const netAmt = calcAmt - lateAmt;
  const hasLate = effMin > 0 && !dlgC?.is_manual;

  if (!open) return null;
  return (
    <div style={OV} onClick={onClose}>
      <div style={MO} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 45%, #334155 100%)', padding: '18px 20px', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.62rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.12em' }}>แก้ไขข้อมูลการลงเวลา</span>
                {dlgOvr && <span style={{ fontSize: '0.6rem', padding: '1px 8px', borderRadius: 10, background: '#f59e0b', color: '#fff', fontWeight: 700 }}>มีการแก้ไข</span>}
              </div>
              <div style={{ fontSize: '1.2rem', fontWeight: 800, color: '#f8fafc', lineHeight: 1.2 }}>{detailData?.name || '—'}</div>
              {dlgC?.checkIn && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '0.78rem', color: '#cbd5e1', fontWeight: 600 }}>{dayjs(dlgC.checkIn).format('DD/MM/YYYY')}</span>
                  {actualHr != null && (
                    <span style={{ padding: '1px 8px', borderRadius: 4, background: actualHr >= 8 ? 'rgba(34,197,94,0.2)' : 'rgba(245,158,11,0.2)', fontSize: '0.72rem', fontWeight: 700, color: actualHr >= 8 ? '#4ade80' : '#fbbf24' }}>
                      {isHourForced && (
                        <span style={{ textDecoration: 'line-through', opacity: 0.6, marginRight: 3 }}>
                          {fmt(rawActualHr, 2)}
                        </span>
                      )}
                      {fmt(actualHr, 2)} ชม.
                    </span>
                  )}
                </div>
              )}
            </div>
            <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', borderRadius: 4, padding: 4, cursor: 'pointer', color: '#cbd5e1', display: 'flex', alignItems: 'center' }}>
              <CloseIcon style={{ fontSize: 18 }} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div style={{ padding: '16px 20px', overflowY: 'auto', flex: 1 }}>
          {/* เวลาเข้าออก + โปรเจค (info) */}
          <SecLabel txt="เวลาเข้าออก & โปรเจค" color="#3b82f6" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            <InfoPanel border="#dbeafe" bg="#f8fbff">
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <AccessTimeIcon style={{ fontSize: 13, color: '#6b7280' }} />
                <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>เวลาเข้า - ออก</span>
              </div>
              <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>{dlgC?.checkInTime || '-'} — {dlgC?.checkOutTime || '?'}</div>
              {rawActualHr != null && (
                <div style={{ fontSize: '0.65rem', color: '#64748b', marginTop: 4 }}>
                  ทำงานจริง {fmt(rawActualHr, 2)} ชม.{lateMin > 0 ? ` · สาย ${lateMin} นาที` : ''}
                </div>
              )}
            </InfoPanel>
            <InfoPanel border="#e2e8f0" bg="#f8fafc">
              <div style={{ display: 'flex', alignItems: 'center', gap: 4, marginBottom: 4 }}>
                <WorkIcon style={{ fontSize: 13, color: '#6b7280' }} />
                <span style={{ fontSize: '0.72rem', color: '#6b7280' }}>โปรเจค</span>
              </div>
              <div style={{ fontWeight: 500, fontSize: '0.875rem', wordBreak: 'break-word' }}>
                {dlgC?.project_name ? `${dlgC.project_name}${dlgC?.project_number ? ` (${dlgC.project_number})` : ''}` : 'ไม่ระบุ'}
              </div>
            </InfoPanel>
          </div>

          {/* แก้ไขเวลา + โปรเจค + ตั้งค่าค่าแรง */}
          {hrMgmt && dlgC && (
            <div style={{ display: 'grid', gridTemplateColumns: '1.1fr 0.9fr', gap: 14, marginBottom: 16 }}>
              {/* แก้ไขเวลา */}
              <InfoPanel border="#dbeafe" bg="#f8fbff">
                <SecLabel txt="แก้ไขเวลาเข้า-ออก & โปรเจค" color="#0ea5e9" />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, marginBottom: 10, alignItems: 'end' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <FRow label="เวลาเข้า" noMargin>
                      <input type="time" value={editCheckIn} onChange={(e) => setEditCheckIn(e.target.value)} style={IS} disabled={checkTimeSaving} />
                    </FRow>
                    <FRow label="เวลาออก" noMargin>
                      <input type="time" value={editCheckOut} onChange={(e) => setEditCheckOut(e.target.value)} style={IS} disabled={checkTimeSaving} />
                    </FRow>
                  </div>
                  <button onClick={saveCheckInTime} disabled={checkTimeSaving || (!editCheckIn && !editCheckOut)}
                    style={{ ...BC, height: 34, borderRadius: 6, fontWeight: 700, opacity: checkTimeSaving || (!editCheckIn && !editCheckOut) ? 0.6 : 1 }}>
                    {checkTimeSaving ? 'กำลังบันทึก...' : 'บันทึกเวลา'}
                  </button>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 8, alignItems: 'end' }}>
                  <FRow label="โปรเจค" noMargin>
                    <select value={projectList.some((p) => String(p._id) === editProjectId) ? editProjectId : ''} onChange={(e) => setEditProjectId(e.target.value)} style={{ ...IS, cursor: 'pointer' }} disabled={checkTimeSaving}>
                      <option value="">— ไม่ระบุ —</option>
                      {projectList.map((p) => (
                        <option key={p._id} value={String(p._id)}>{p.project_number ? `${p.project_number} — ` : ''}{p.name}</option>
                      ))}
                    </select>
                  </FRow>
                  <button onClick={saveProject} disabled={checkTimeSaving}
                    style={{ ...BC, height: 34, borderRadius: 6, fontWeight: 700, opacity: checkTimeSaving ? 0.6 : 1 }}>
                    {checkTimeSaving ? 'กำลังบันทึก...' : 'บันทึกโปรเจค'}
                  </button>
                </div>
              </InfoPanel>

              {/* ตั้งค่าค่าแรง */}
              {(onSalaryOverride || onSkipLateDeduct) && (
                <InfoPanel border="#e2e8f0" bg="#f8fafc">
                  <SecLabel txt="ตั้งค่าค่าแรง" color="#6366f1" />
                  {/* Field allowance — โชว์เฉพาะพนักงานที่มีสิทธิ์รับค่าออกสนาม (ตั้งไว้ในโปรไฟล์พนักงาน) */}
                  {detailData?.faEnabled && (
                    <label style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, cursor: 'pointer', padding: '8px 10px', background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 6 }}>
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#374151' }}>รับค่าออกสนาม</div>
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>ให้ค่าเดินทาง/ออกสนามสำหรับวันนี้</div>
                      </div>
                      <input type="checkbox" checked={dlgFieldAllow} onChange={(e) => saveFieldAllowDay(e.target.checked)} />
                    </label>
                  )}

                  {onSkipLateDeduct && dlgC && salaryMode !== 'work-time' && (
                    <label style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, cursor: skipLateSaving ? 'not-allowed' : 'pointer', padding: '8px 10px', background: dlgIsSkipped ? 'rgba(249,115,22,0.08)' : '#fff', border: `1.5px solid ${dlgIsSkipped ? '#f97316' : '#e5e7eb'}`, borderRadius: 6 }}>
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: dlgIsSkipped ? '#ea580c' : '#374151' }}>{dlgIsSkipped ? '✓ ยกเว้นหักสายแล้ว' : 'ยกเว้นหักสาย'}</div>
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{dlgIsSkipped ? 'คลิกเพื่อกลับมาหักสายตามปกติ' : 'เปิดเพื่อไม่หักเงินกรณีมาสาย'}</div>
                      </div>
                      <input type="checkbox" checked={dlgIsSkipped} onChange={(e) => saveSkipLate(e.target.checked)} disabled={skipLateSaving} />
                    </label>
                  )}

                  {(rawActualHr ?? 0) < 8 && salaryMode !== 'late-deduct' && (
                    <label style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 8, cursor: 'pointer', padding: '8px 10px', background: forceFullHours ? 'rgba(124,58,237,0.08)' : '#fff', border: `1.5px solid ${forceFullHours ? '#7c3aed' : '#e5e7eb'}`, borderRadius: 6 }}>
                      <div>
                        <div style={{ fontSize: '0.82rem', fontWeight: 700, color: forceFullHours ? '#7c3aed' : '#374151' }}>{forceFullHours ? '✓ นับเป็น 8 ชม.' : 'นับชั่วโมงเป็น 8 ชม.'}</div>
                        <div style={{ fontSize: '0.68rem', color: '#9ca3af' }}>{forceFullHours ? `ทำงานจริง ${fmt(rawActualHr ?? 0, 2)} ชม. → คิดเงินเป็น 8 ชม.` : 'เปิดถ้าลงเวลาไม่ได้เพราะเหตุฉุกเฉิน'}</div>
                      </div>
                      <input type="checkbox" checked={forceFullHours} onChange={(e) => setForceFullHours(e.target.checked)} />
                    </label>
                  )}

                  {onSalaryOverride && (
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
                      <FRow label="อัตราค่าแรง/วัน (฿)">
                        <input type="number" value={editAmtManual ?? String(detailData?.spd || 0)} onChange={(e) => setEditAmtManual(e.target.value)} style={{ ...IS, ...(isDrSuspicious ? { border: '1.5px solid #dc2626' } : {}) }} disabled={saving} />
                        {editAmtManual == null && <div style={{ fontSize: '0.68rem', color: '#9ca3af', marginTop: 2 }}>ค่าเริ่มต้น ฿{fmt(detailData?.spd)}</div>}
                        {isDrSuspicious && (
                          <div style={{ fontSize: '0.68rem', color: '#dc2626', marginTop: 2, fontWeight: 600 }}>
                            ⚠ ต่างจากค่าแรงปกติของพนักงานคนนี้ (฿{fmt(origDr, 0)}/วัน) เกิน {DR_SUSPICIOUS_MULTIPLIER} เท่า เช็คให้ดีว่าพิมพ์ถูกไหม
                          </div>
                        )}
                      </FRow>
                      <FRow label="ตัวคูณ">
                        <select value={detailRate} onChange={(e) => setDetailRate(Number(e.target.value))} style={{ ...IS, cursor: 'pointer' }} disabled={saving}>
                          {RATE_OPTIONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                        </select>
                      </FRow>
                    </div>
                  )}
                </InfoPanel>
              )}
            </div>
          )}

          {/* สรุปค่าแรง */}
          <SecLabel txt="สรุปค่าแรง" color="#2563eb" />
          <div style={{ display: 'grid', gridTemplateColumns: `repeat(${(salaryMode !== 'work-time' ? 1 : 0) + (salaryMode !== 'late-deduct' ? 1 : 0) + (hasLate && salaryMode !== 'work-time' ? 1 : 0)}, minmax(0,1fr))`, gap: 8, marginBottom: 16 }}>
            {(() => {
              const realH = forceFullHours ? 8 : rawActualHr ?? 0;
              const actualWage = Number((Math.min(realH, 8) * detailRate * (currentDr / 8)).toFixed(2));
              return (
                <>
                  {salaryMode !== 'work-time' && <StatCard label="ค่าแรงเต็มวัน (8 ชม.)" amount={calcAmt} formula={`8h × ×${detailRate} × ฿${fmt(currentDr, 0)}/วัน`} extra={`฿${fmt(hrRate)}/ชม.`} color="#2563eb" strikeAmt={isModified ? origAmt : null} />}
                  {salaryMode !== 'late-deduct' && <StatCard label={`ค่าแรงตามชม.จริง (${fmt(realH, 2)} ชม.)`} amount={actualWage} formula={`${Math.min(realH, 8)}h × ×${detailRate} × ฿${fmt(currentDr, 0)}/วัน`} extra={forceFullHours ? 'นับเป็น 8 ชม. (เหตุฉุกเฉิน)' : null} color="#7c3aed" />}
                  {hasLate && salaryMode !== 'work-time' && (
                    dlgIsSkipped
                      ? <StatCard label="หลังหักสาย" amount={calcAmt} formula="✓ ยกเว้นหักสายแล้ว" color="#16a34a" />
                      : <StatCard label="หลังหักสาย" amount={netAmt} formula={`−฿${fmt(lateAmt)} (สาย ${effMin} นาที)`} extra={`฿${fmt(calcAmt)} − ฿${fmt(lateAmt)} = ฿${fmt(netAmt)}`} color="#f97316" />
                  )}
                </>
              );
            })()}
          </div>

          {/* ค่าออกสนาม */}
          {detailData?.faEnabled && dlgFieldAllow && (() => {
            const realHours = forceFullHours ? 8 : actualHr ?? (parseFloat(detailHours) || 0);
            const faDay = detailData?.faAmt || 0;
            const faHrRate = faDay / 8;
            const faHours = Math.min(realHours, 8);
            const fieldAmt = Number((faHours * detailRate * faHrRate).toFixed(2));
            const dlgDay = dlgC?.checkIn ? dayjs(dlgC.checkIn).date() : null;
            const manEntry = dlgDay != null ? getEntry(detailData?.empId, dlgDay) : null;
            const { otHours, otRate } = calcOtForDay(dlgC, manEntry, 0);
            const otFieldAmt =
              otHours > 0 ? Number((otHours * otRate * faHrRate).toFixed(2)) : 0;
            const hasOt = otHours > 0;
            const totalFieldAmt = Number((fieldAmt + otFieldAmt).toFixed(2));
            return (
              <div style={{ marginBottom: 16 }}>
                <SecLabel txt="ค่าออกสนาม" color="#15803d" />
                <div style={{ display: 'grid', gridTemplateColumns: hasOt ? `repeat(${hasOt ? 3 : 2}, minmax(0,1fr))` : '1fr 1fr', gap: 8 }}>
                  <StatCard label={`ค่าแรงออกสนาม (${faHours}h)`} amount={fieldAmt} formula={`${faHours}h × ×${detailRate} × ฿${fmt(faDay, 0)}/วัน`} color="#15803d" />
                  {hasOt && <StatCard label="OT ออกสนาม" amount={otFieldAmt} formula={`${fmt(otHours, 2)}h × ×${otRate} × ฿${fmt(faHrRate, 2)}/ชม.`} color="#0d9488" />}
                  <StatCard label="รวมค่าออกสนาม" amount={totalFieldAmt} formula={hasOt ? `฿${fmt(fieldAmt)} + ฿${fmt(otFieldAmt)}` : `฿${fmt(fieldAmt)}`} color="#065f46" />
                </div>
              </div>
            );
          })()}

          {/* การ์ด "เพิ่มโดย" (ฟ้า) กับ "แก้ไขโดย" (เหลือง) — คนละเหตุการณ์ โชว์พร้อมกันได้ทั้งคู่ */}
          {(dlgShowAddedCard || dlgHasEdit) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {dlgShowAddedCard && (
                <InfoPanel border="rgba(59,130,246,0.35)" bg="rgba(59,130,246,0.08)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <EditNoteIcon style={{ fontSize: 14, color: '#2563eb' }} />
                    <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      {dlgAddedByLabel}
                    </span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontSize: '0.6rem', color: '#78716c', marginBottom: 3 }}>เพิ่มโดย</div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e40af' }}>{dlgAddedBy || '—'}</div>
                    </div>
                    <div>
                      <div style={{ fontSize: '0.6rem', color: '#78716c', marginBottom: 3 }}>เมื่อ</div>
                      <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#1e40af' }}>
                        {dlgAddedAt ? dayjs(dlgAddedAt).format('DD/MM/YY HH:mm') : '—'}
                      </div>
                    </div>
                  </div>
                </InfoPanel>
              )}

              {dlgHasEdit && (
                <InfoPanel border="rgba(245,158,11,0.35)" bg="rgba(245,158,11,0.08)">
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                    <EditNoteIcon style={{ fontSize: 14, color: '#d97706' }} />
                    <span style={{ fontSize: '0.65rem', fontWeight: 800, color: '#92400e', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                      ประวัติการแก้ไข
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {dlgEditEntries.map((en) => (
                      <div key={en.label} style={{ display: 'grid', gridTemplateColumns: '70px 1fr 1fr', gap: 12, alignItems: 'start' }}>
                        <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#b45309', paddingTop: 1 }}>{en.label}</div>
                        <div>
                          <div style={{ fontSize: '0.6rem', color: '#78716c', marginBottom: 2 }}>แก้ไขโดย</div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 700, color: '#92400e' }}>{en.by || '—'}</div>
                        </div>
                        <div>
                          <div style={{ fontSize: '0.6rem', color: '#78716c', marginBottom: 2 }}>เมื่อ</div>
                          <div style={{ fontSize: '0.82rem', fontWeight: 600, color: '#92400e' }}>
                            {en.at ? dayjs(en.at).format('DD/MM/YY HH:mm') : '—'}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </InfoPanel>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e2e8f0', background: '#fcfdff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {dlgOvr && hrMgmt && (dlgC?.is_manual
              ? <button style={BO} onClick={deleteManualFromDialog} disabled={saving}>ลบชั่วโมงที่เพิ่ม</button>
              : <button style={BO} onClick={clearOverride} disabled={saving}>ล้างการแก้ไข</button>
            )}
            {dlgTimeOvr && hrMgmt && onCheckInTimeClear && <button style={BW} onClick={clearTimeOverride} disabled={saving}>ล้างการแก้ไขเวลา</button>}
            {dlgProjOvr && hrMgmt && onProjectClear && <button style={BW} onClick={clearProjectOverride} disabled={saving}>ล้างการแก้ไขโปรเจค</button>}
            {!dlgOvr && hrMgmt && onManualDelete && dlgC && detailData?.empId && getEntry(detailData.empId, dayjs(dlgC.checkIn).date()) && (
              <button style={BO} onClick={deleteManualFromDialog} disabled={saving}>ลบชั่วโมงการทำงาน</button>
            )}
            {hrMgmt && onHourOverride && dlgC && !dlgC?.is_manual && !dlgHoursDeleted && (dlgC?.totalHour || 0) > 0 && (
              <button style={BO} onClick={deleteWorkHours} disabled={saving}>ลบชั่วโมงทำงาน</button>
            )}
            {hrMgmt && onHourOverride && dlgHoursDeleted && <button style={BG} onClick={restoreWorkHours} disabled={saving}>คืนชั่วโมงทำงาน</button>}
            {hrMgmt && onHourOverride && isHourForced && <button style={BW} onClick={clearForceFullHours} disabled={saving}>ล้างการติ๊ก "นับเป็น 8 ชม."</button>}
            {hrMgmt && onOtOverride && dlgHasOt && !dlgOtDisabled && <button style={BO} onClick={deleteOtHours} disabled={saving}>ลบชั่วโมง OT</button>}
            {hrMgmt && onOtOverride && dlgOtDisabled && <button style={BG} onClick={restoreOtHours} disabled={saving}>คืนชั่วโมง OT</button>}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={BC} onClick={onClose} disabled={saving}>ปิด</button>
            {hrMgmt && onSalaryOverride && (
              <button style={{ ...BP, opacity: saving ? 0.7 : 1, minWidth: 112 }} onClick={saveOverride} disabled={saving}>
                {saving ? 'กำลังบันทึก...' : 'บันทึกการแก้ไข'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default SalaryOverrideDialog;
